import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pause, AlertCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import SessionProgressBar from './components/SessionProgressBar';
import CurrentActivityDisplay from './components/CurrentActivityDisplay';
import SessionControls from './components/SessionControls';
import MotivationalFeedback from './components/MotivationalFeedback';

/**
 * Active Practice Session Interface
 * Executes custom warm-up workflows with real-time guidance and progress tracking
 */
export default function ActivePracticeSessionInterface() {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionConfig = location?.state;

  // Session state
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [segmentTimeRemaining, setSegmentTimeRemaining] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('ready'); // ready, active, paused, completed
  const [completedSegments, setCompletedSegments] = useState([]);
  const [totalXPEarned, setTotalXPEarned] = useState(0);

  // Timer refs
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Validate session config
  useEffect(() => {
    if (!sessionConfig || !sessionConfig?.workflow || sessionConfig?.workflow?.length === 0) {
      alert('Invalid session configuration');
      navigate('/custom-practice-session-builder');
    }
  }, [sessionConfig, navigate]);

  // Initialize segment timing
  useEffect(() => {
    if (sessionConfig?.workflow?.[currentSegmentIndex]) {
      const duration = sessionConfig?.workflow?.[currentSegmentIndex]?.duration * 60; // Convert to seconds
      setSegmentTimeRemaining(duration);
    }
  }, [currentSegmentIndex, sessionConfig]);

  // Timer logic
  useEffect(() => {
    if (sessionStatus === 'active') {
      timerRef.current = setInterval(() => {
        setSegmentTimeRemaining((prev) => {
          if (prev <= 1) {
            handleSegmentComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef?.current) {
        clearInterval(timerRef?.current);
      }
    }

    return () => {
      if (timerRef?.current) {
        clearInterval(timerRef?.current);
      }
    };
  }, [sessionStatus]);

  // Handle segment completion
  const handleSegmentComplete = useCallback(() => {
    const currentSegment = sessionConfig?.workflow?.[currentSegmentIndex];
    
    // Mark segment as completed
    setCompletedSegments([...completedSegments, currentSegmentIndex]);
    
    // Calculate XP for segment
    const segmentXP = Math.floor((currentSegment?.duration || 5) * 3);
    setTotalXPEarned(totalXPEarned + segmentXP);

    // Show completion feedback
    showSegmentCompletionFeedback();

    // Move to next segment or complete session
    if (currentSegmentIndex < sessionConfig?.workflow?.length - 1) {
      setCurrentSegmentIndex(currentSegmentIndex + 1);
    } else {
      handleSessionComplete();
    }
  }, [currentSegmentIndex, sessionConfig, completedSegments, totalXPEarned]);

  // Handle session completion
  const handleSessionComplete = () => {
    setSessionStatus('completed');
    
    // Epic celebration
    confetti({
      particleCount: 150,
      spread: 180,
      origin: { y: 0.5 },
      colors: ['#ff6b6b', '#feca57', '#1dd1a1', '#48dbfb', '#ee5a6f', '#5f27cd']
    });

    // Save session completion (localStorage for now)
    saveSessionCompletion();
  };

  // Show segment completion feedback
  const showSegmentCompletionFeedback = () => {
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#1dd1a1', '#10ac84', '#48dbfb']
    });
  };

  // Save session completion
  const saveSessionCompletion = () => {
    try {
      const completionData = {
        sessionName: sessionConfig?.sessionName,
        completedAt: new Date()?.toISOString(),
        duration: sessionConfig?.workflow?.reduce((sum, seg) => sum + seg?.duration, 0),
        xpEarned: totalXPEarned,
        segmentsCompleted: completedSegments?.length
      };

      const existingCompletions = JSON.parse(localStorage?.getItem('practice_completions') || '[]');
      localStorage?.setItem('practice_completions', JSON.stringify([...existingCompletions, completionData]));
    } catch (error) {
      console.error('Error saving session completion:', error);
    }
  };

  // Start session
  const handleStart = () => {
    setSessionStatus('active');
    startTimeRef.current = Date.now();
  };

  // Pause session
  const handlePause = () => {
    setSessionStatus('paused');
  };

  // Resume session
  const handleResume = () => {
    setSessionStatus('active');
  };

  // Stop session
  const handleStop = () => {
    if (window.confirm('Are you sure you want to stop this session? Progress will not be saved.')) {
      navigate('/custom-practice-session-builder');
    }
  };

  // Skip segment
  const handleSkip = () => {
    if (currentSegmentIndex < sessionConfig?.workflow?.length - 1) {
      setCurrentSegmentIndex(currentSegmentIndex + 1);
    }
  };

  // Exit to builder
  const handleExit = () => {
    navigate('/custom-practice-session-builder');
  };

  if (!sessionConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d] flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Session Not Found</h2>
          <p className="text-white/80 mb-4">Unable to load practice session configuration.</p>
          <button
            onClick={() => navigate('/custom-practice-session-builder')}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
          >
            Return to Session Builder
          </button>
        </div>
      </div>
    );
  }

  const currentSegment = sessionConfig?.workflow?.[currentSegmentIndex];
  const overallProgress = ((currentSegmentIndex + (1 - (segmentTimeRemaining / (currentSegment?.duration * 60)))) / sessionConfig?.workflow?.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Session Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {sessionConfig?.sessionName || 'Practice Session'}
              </h1>
              <p className="text-white/80">
                {sessionStatus === 'completed' 
                  ? '🎉 Session Complete!' 
                  : `Segment ${currentSegmentIndex + 1} of ${sessionConfig?.workflow?.length}`
                }
              </p>
            </div>
            <button
              onClick={handleExit}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
            >
              Exit
            </button>
          </div>
        </div>

        {/* Session Progress Bar */}
        <SessionProgressBar
          workflow={sessionConfig?.workflow}
          currentSegmentIndex={currentSegmentIndex}
          segmentProgress={1 - (segmentTimeRemaining / (currentSegment?.duration * 60))}
          overallProgress={overallProgress}
        />

        {/* Main Content */}
        {sessionStatus !== 'completed' ? (
          <>
            {/* Current Activity Display */}
            <CurrentActivityDisplay
              currentSegment={currentSegment}
              segmentTimeRemaining={segmentTimeRemaining}
              sessionStatus={sessionStatus}
            />

            {/* Session Controls */}
            <SessionControls
              sessionStatus={sessionStatus}
              onStart={handleStart}
              onPause={handlePause}
              onResume={handleResume}
              onStop={handleStop}
              onSkip={handleSkip}
              canSkip={currentSegmentIndex < sessionConfig?.workflow?.length - 1}
            />

            {/* Motivational Feedback */}
            <MotivationalFeedback
              totalXPEarned={totalXPEarned}
              completedSegments={completedSegments?.length}
              totalSegments={sessionConfig?.workflow?.length}
            />
          </>
        ) : (
          /* Session Complete Screen */
          (<div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-white mb-4">
              Curtain Call Summary 🎬
            </h2>
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-4xl font-bold text-white mb-2">{totalXPEarned}</div>
                <div className="text-white/60 text-sm">XP Earned</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-4xl font-bold text-white mb-2">{completedSegments?.length}</div>
                <div className="text-white/60 text-sm">Segments Completed</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-4xl font-bold text-white mb-2">
                  {sessionConfig?.workflow?.reduce((sum, seg) => sum + seg?.duration, 0)}
                </div>
                <div className="text-white/60 text-sm">Minutes Practiced</div>
              </div>
            </div>
            <p className="text-white/80 text-lg mb-8">
              👏 Nice work! You've completed your practice session.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/custom-practice-session-builder')}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold transition-all"
              >
                Create New Session
              </button>
              <button
                onClick={() => navigate('/the-stage-student-dashboard')}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          </div>)
        )}
      </div>
    </div>
  );
}